import { NotificationPreferencesApiService } from '@core/api/notification-preferences-api.service';
import { userSaveError } from '../user-save-error';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  catchError,
  exhaustMap,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import {
  DialogComponent,
  IDialogComponentData,
  openConfirmDialog,
} from 'ngx-mt-ui-lib/components/dialog';
import { UserApiService } from '@core/api/user-api.service';
import { UserActions } from './user.actions';
import { selectUserHasChanges, selectUserIsLoading } from './user.selectors';
import { UserGridService } from '../user-grid/user-grid.service';
import { UserTempPasswordDialogComponent } from '../user-card/user-temprary-password-dialog/user-temp-password-dialog.component';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly api = inject(UserApiService);
  private readonly preferences = inject(NotificationPreferencesApiService);
  private readonly dialog = inject(Dialog);
  private readonly grid = inject(UserGridService);
  readonly select$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.attemptSelectUser, UserActions.closeUser),
      withLatestFrom(
        this.store.select(selectUserHasChanges),
        this.store.select(selectUserIsLoading),
      ),
      exhaustMap(([action, changed, loading]) => {
        if (loading) return EMPTY;
        const id = 'id' in action ? action.id : null;
        const accepted = id
          ? UserActions.selectUser({ id })
          : UserActions.clearUser();
        return changed
          ? openConfirmDialog(this.dialog).closed.pipe(
              switchMap((ok) => (ok ? of(accepted) : EMPTY)),
            )
          : of(accepted);
      }),
    ),
  );
  readonly selected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.selectUser),
      switchMap(({ id }) =>
        id && id !== 'new' ? of(UserActions.requestUserInfo({ id })) : EMPTY,
      ),
    ),
  );
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.requestUserInfo),
      switchMap(({ id }) =>
        this.api.getInfo(id).pipe(
          map((user) => UserActions.requestUserInfoSuccess({ id, user })),
          catchError(() =>
            of(
              UserActions.requestUserInfoError({
                id,
                error:
                  'Не удалось загрузить пользователя. Проверьте доступ и повторите попытку.',
              }),
            ),
          ),
        ),
      ),
    ),
  );
  readonly save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.save),
      exhaustMap(({ id, params }) =>
        (id === 'new'
          ? this.api.createUser(params).pipe(
              map((result) => {
                this.dialog.open(UserTempPasswordDialogComponent, {
                  data: result,
                  disableClose: true,
                });
                return result.userId;
              }),
            )
          : this.api.updateUser(id, params).pipe(map(() => id))
        ).pipe(
          switchMap((userId) =>
            !params.notificationChange
              ? of(userId)
              : this.preferences
                  .updateUser(
                    userId,
                    params.notificationChange.expectedRevision,
                    params.notificationChange.preferences,
                  )
                  .pipe(
                    map(() => userId),
                    catchError((error) => {
                      this.dialog.open<boolean, IDialogComponentData>(
                        DialogComponent,
                        {
                          data: {
                            title: 'Настройки уведомлений не сохранены',
                            description:
                              error.status === 409
                                ? 'Профиль сохранён. Настройки уведомлений уже изменены другим пользователем. Проверьте актуальные значения и повторите изменение.'
                                : 'Профиль сохранён. Сервис уведомлений не подтвердил изменение настроек. Обновите карточку и повторите изменение уведомлений.',
                            buttons: [
                              { title: 'Понятно', onClick: () => true },
                            ],
                          },
                        },
                      );
                      return of(userId);
                    }),
                  ),
          ),
          map((userId) => UserActions.saved({ id: userId })),
          catchError((error) =>
            of(
              UserActions.failed({
                error: userSaveError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );
  readonly saved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.saved),
      tap(() => this.grid.refreshGrid()),
      map(({ id }) => UserActions.requestUserInfo({ id })),
    ),
  );
}
